/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 08/09/2025 - 21:24:03
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 08/09/2025
    * - Author          : 
    * - Modification    : 
**/

import "./App.css";
import { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import MapPage from "./pages/Map/MapPage";
import Dashboard from "./pages/dashboard";
import NewProject from "./pages/newProject";
import ActiveProjects from "./pages/activeProjects";
import Trash from "./pages/trash";
import ArchieveProject from "./pages/archievedprojects";
import MyReports from "./pages/myreports";
import Start from "./pages/start";
import Login from "./pages/login";
import Registered from "./pages/registered";
import Register from "./pages/register";
import ResetPassword from "./pages/resetPassword";
import LinkSend from "./pages/linkSend";
import ChangePassword from "./pages/changePassword";
import ProjectOverview from "./pages/projectOverview";
import UnpaidProject from "./pages/unpaidProject";
import PaidProject from "./pages/paidProject";
import AnalysisService from "./pages/analysisService";
import LandingPage from "./pages/landingPage";
import Payment from "./pages/payment";
import AnalysisPayment from "./pages/analysispayment";
import AnalysisSuccess from "./pages/analysispayment/AnalysisPaymentSuccess";
import { auth } from "./firebase";
import Newpdf from "./pages/newPdf";
import ShowToken from "./pages/showToken/ShowToken";
import Security from "./pages/security";
import ProfileSetting from "./pages/profileSetting";
import Biling from "./pages/biling";
import VideoTutorials from "./pages/demoVideos";
import { onAuthStateChanged } from "firebase/auth";
import { useState } from "react";
import DeleteProfile from "./pages/deleteProfile";
import Clarity from "@microsoft/clarity";
import './utils/i18n';
import LoadingSpinner from './components/shared/LoadingSpinner';
import { locationapi } from "./api/locationapi";

function App() {
  const [user, setUserData] = useState("");
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        setUserData(user.uid);
        
        try {
          await locationapi.trackUserLocation();
          const token = await user.getIdToken();
          localStorage.setItem('firebaseToken', token);
        } catch (error) {
          console.error("Error starting session tracking:", error);
        }
      } else {
        const sessionId = localStorage.getItem('sessionId');
        if (sessionId) {
          try {
            await locationapi.endSession();
          } catch (error) {
            console.error("Error ending session:", error);
          }
          localStorage.removeItem('sessionId');
          localStorage.removeItem('firebaseToken');
        }
        
        setUserData("");
      }
    });
    
    return () => {
      unsubscribe();
      const sessionId = localStorage.getItem('sessionId');
      if (sessionId && auth.currentUser) {
        locationapi.endSession().catch((err: any) => {
          console.error("Error ending session on unmount:", err);
        });
      }
    };
  }, []);

  useEffect(() => {
    if (user) {
      const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
      const handleActivity = () => {
        if (locationapi.resetActivityTimer) {
          locationapi.resetActivityTimer();
        }
      };
      activityEvents.forEach(event => {
        window.addEventListener(event, handleActivity);
      });
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          const sessionId = localStorage.getItem('sessionId');
          if (sessionId) {
            locationapi.updateSessionActivity().catch((err: any) => {
              console.error("Error updating session activity:", err);
            });
          }
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleActivity);
        });
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user]);

  useEffect(() => {
    const projectId = "p9466km2wo";
    Clarity.init(projectId);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const sessionId = localStorage.getItem('sessionId');
      const token = localStorage.getItem('firebaseToken');
      
      if (sessionId && token) {
        console.log('Window closing, sending end-session beacon');
        
        // Create a proper JSON blob for sendBeacon
        const blob = new Blob([JSON.stringify({ token, sessionId })], 
          { type: 'application/json' });
        
        // The URL should match your backend endpoint
        navigator.sendBeacon(
          'https://stripe.pv-glarecheck.com/track-location/end-session',
          blob
        );
      }
    };
    
    // Add event listener for beforeunload
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Clean up
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <Suspense fallback={<LoadingSpinner />}>
    <BrowserRouter>
      <Routes>
        {/* <Route path="/" element={<LandingPage />} /> */}
        <Route path="/map_page/:projectId" element={<MapPage />} />
        {user && <Route path="/dashboard" element={<Dashboard />} />}
        <Route path="/newproject" element={<NewProject />} />
        <Route path="/activeproject" element={<ActiveProjects />} />
        <Route path="/trash" element={<Trash />} />
        <Route path="/archievedProject" element={<ArchieveProject />} />
        <Route path="/myreports" element={<MyReports />} />
        <Route path="/analysisservice" element={<AnalysisService />} />
        <Route path="/" element={<Start />} />
        <Route path="/login" element={<Login />} />
        <Route path="/registered" element={<Registered />} />
        <Route path="/register" element={<Register />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/linksend" element={<LinkSend />} />
        <Route path="/changepassword" element={<ChangePassword />} />
        <Route path="/projectoverview/:projectId" element={<ProjectOverview />} />
        <Route path="/unpaidproject/:projectId" element={<UnpaidProject />} />
        <Route path="/paidproject" element={<PaidProject />} />
        <Route path="/payment" element={<Payment />} />
        <Route path="/analysispayment" element={<AnalysisPayment />} />
        <Route path="//analysissuccess" element={<AnalysisSuccess />} />
        <Route path="/pdf" element={<Newpdf />} />
        <Route path="/createTokens" element={<ShowToken />} />
        <Route path="/security" element={<Security />} />
        <Route path="/profilesetting" element={<ProfileSetting />} />
        <Route path="/delprofile" element={<DeleteProfile />} />
        <Route path="/biling" element={<Biling />} />
        <Route path="/tutorial" element={<VideoTutorials />} />
        <Route path="/*" element={<Start />} />
      </Routes>
    </BrowserRouter>
    </Suspense>
  );
}

export default App;